import { useCallback, useMemo } from "react";

import {
  tooltipFormatter,
  getDonutDataTotal,
  getDonutDataValue,
  getValuePercent,
} from "@/components/Elements/Charts/helpers";
import { CHART_PERCENT_PRECISION } from "@/constants/Charts";

const DEFAULT_CHART_LEFT_POSITION = "35%";

export const useDonutChartOptions = ({
  data,
  formatTotal,
  formatName,
  formatLabel,
  formatTooltipValue,
  controlled,
  chartLeftPosition,
  hideLegend,
  hideTotal,
  hideLabels,
  customOptions,
}) => {
  const total = useMemo(() => getDonutDataTotal(data), [data]);

  const formatTooltip = useCallback(
    ({ name, value }) =>
      tooltipFormatter({
        name: formatName(name),
        value: formatTooltipValue(value),
        percent: getValuePercent(value, total),
      }),
    [formatName, formatTooltipValue]
  );

  const graphLeftPosition = useMemo(() => {
    if (chartLeftPosition) return chartLeftPosition;
    if (hideLegend) return "50%"; // center chart
    return DEFAULT_CHART_LEFT_POSITION; // push chart to the left to show legend
  }, [chartLeftPosition, hideLegend]);

  const title = useMemo(() => {
    if (hideTotal) return undefined;

    return {
      text: formatTotal(total),
      top: "middle",
      left: graphLeftPosition,
      textAlign: "center",
      padding: 0,
      ...customOptions.title,
    };
  }, [hideTotal, customOptions, formatTotal, graphLeftPosition]);

  const legend = useMemo(() => {
    if (hideLegend) return undefined;

    return {
      selected: controlled?.selectedLegend, // selected is defined only if widget is controlled by parent component
      formatter: formatName,
      orient: "vertical",
      top: "center",
      right: 0,
      align: "right",
      itemWidth: 10,
      itemHeight: 10,
      borderRadius: "100%",
      icon: "circle",
      tooltip: {
        show: true,
        formatter: ({ name }) => {
          const value = getDonutDataValue(data, name);
          const percent = getValuePercent(value, total);
          return formatTooltip({ name, value, percent });
        },
      },
      ...customOptions.legend,
    };
  }, [hideLegend, customOptions, controlled, formatName, formatTooltip]);

  const label = useMemo(() => {
    if (hideLabels) return { show: false };

    return {
      show: true,
      position: "outside",
      overflow: "break",
      formatter: formatLabel,
      cursor: "default",
    };
  }, [hideLabels, formatLabel]);

  return useMemo(
    () => ({
      title,
      legend,
      series: {
        type: "pie",
        center: [graphLeftPosition, "50%"],
        radius: ["40%", "65%"],
        avoidLabelOverlap: true,
        percentPrecision: CHART_PERCENT_PRECISION,
        minShowLabelAngle: 1, // hide label for values = 0
        label,
        data: data?.map(({ color, ...entry }) => ({
          ...entry,
          itemStyle: { color },
        })),
        ...customOptions.series,
      },
      tooltip: {
        trigger: "item",
        appendToBody: true,
        formatter: formatTooltip,
        ...customOptions.tooltip,
      },
    }),
    [
      data,
      title,
      legend,
      label,
      graphLeftPosition,
      customOptions,
      formatTooltip,
    ]
  );
};
